import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import DosRejectDialog from "./DosRejectDialog";
import {
  approveDos,
  convertDos,
  deleteDos,
  dosPdf,
  rejectDos,
  submitDos,
} from "../../api/dosApi";
import { getQuotationById } from "../../api/quotationApi";
import { getAuthUser } from "../../api/authApi";
import { isForbiddenError } from "../../lib/axios";
import { getApiErrorMessage, getHttpStatus } from "../../lib/httpError";
import { getDosActions } from "../../lib/dosActions";
import { isSalesEditLocked } from "../../lib/roleAccess";
import { showConfirm, showError, showSuccess } from "../../utils/ alert";
import type { DosConvertResult, DosDetail } from "../../type/dos";

/** Lama object URL PDF dipertahankan sebelum dibebaskan (tab baru perlu memuatnya dulu). */
export const PDF_URL_REVOKE_DELAY_MS = 60_000;

type ActionName = "submit" | "delete" | "approve" | "reject" | "convert" | "pdf";

interface DosActionsProps {
  dos: DosDetail;
  /** Muat ulang detail (setelah sukses, atau setelah 409/404 karena status berubah). */
  onChanged: () => void;
  onDeleted: () => void;
}

const baseButton =
  "flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50";

export default function DosActions({ dos, onChanged, onDeleted }: DosActionsProps) {
  const navigate = useNavigate();
  const user = getAuthUser();
  const actions = getDosActions(user?.role, dos.status?.code, dos.created_by?.id === user?.id);

  const [pendingAction, setPendingAction] = useState<ActionName | null>(null);
  const [checkingQuotation, setCheckingQuotation] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const busy = pendingAction !== null || checkingQuotation;

  if (!Object.values(actions).some(Boolean)) return null;

  const handleError = async (error: unknown, fallback: string) => {
    if (!isForbiddenError(error)) {
      showError("Failed", await getApiErrorMessage(error, fallback));
    }
    // Status bisa sudah diubah pengguna lain -> muat ulang detail.
    const status = getHttpStatus(error);
    if (status === 409 || status === 404) onChanged();
  };

  const run = async (
    name: ActionName,
    request: () => Promise<unknown>,
    successMessage: string,
    fallback: string
  ) => {
    try {
      setPendingAction(name);
      await request();
      await showSuccess(successMessage);
      if (name === "delete") onDeleted();
      else onChanged();
    } catch (error) {
      await handleError(error, fallback);
    } finally {
      setPendingAction(null);
    }
  };

  const handleSubmit = async () => {
    if (!(await showConfirm(`Submit DOS ${dos.dos_no} for approval?`))) return;
    run("submit", () => submitDos(dos.id), "DOS submitted for approval.", "Failed to submit DOS.");
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm(
      `Delete DOS ${dos.dos_no}?`,
      "This action is permanent and cannot be undone."
    );
    if (!confirmed) return;
    run("delete", () => deleteDos(dos.id), "DOS deleted successfully.", "Failed to delete DOS.");
  };

  const handleApprove = async () => {
    if (!(await showConfirm(`Approve DOS ${dos.dos_no}?`))) return;
    run("approve", () => approveDos(dos.id), "DOS approved.", "Failed to approve DOS.");
  };

  const handleReject = async (reason: string) => {
    try {
      setPendingAction("reject");
      await rejectDos(dos.id, reason);
      setRejectOpen(false);
      await showSuccess("DOS rejected.");
      onChanged();
    } catch (error) {
      setRejectOpen(false);
      await handleError(error, "Failed to reject DOS.");
    } finally {
      setPendingAction(null);
    }
  };

  /** Teks peringatan konfirmasi convert berdasarkan status quotation terkait. */
  const getQuotationWarning = async (): Promise<string> => {
    if (!dos.quotation) return "";

    try {
      setCheckingQuotation(true);
      const response = await getQuotationById(dos.quotation.id);
      const data = response.data.data ?? response.data;
      const code: string | undefined = data?.status?.code;

      return isSalesEditLocked(code)
        ? `Warning: quotation ${dos.quotation.quotation_no} already has status ${
            data.status?.name ?? code
          }. Another Delivery Order may already exist for this quotation.`
        : "";
    } catch {
      return `Warning: the status of quotation ${dos.quotation.quotation_no} could not be checked.`;
    } finally {
      setCheckingQuotation(false);
    }
  };

  const handleConvert = async () => {
    const warning = await getQuotationWarning();
    const confirmed = await showConfirm(
      `Create an official Delivery Order from DOS ${dos.dos_no}?`,
      warning || undefined
    );
    if (!confirmed) return;

    try {
      setPendingAction("convert");
      const response = await convertDos(dos.id);
      const result: DosConvertResult = response.data.data;

      const quotationNote = dos.quotation
        ? result.quotation_status_updated
          ? " Quotation status updated to DO Created."
          : " Quotation status unchanged."
        : "";

      await showSuccess(
        "Official Delivery Order created.",
        `Delivery Order No: ${result.delivery_order_no}.${quotationNote}`
      );
      onChanged();
    } catch (error) {
      await handleError(error, "Failed to create official Delivery Order.");
    } finally {
      setPendingAction(null);
    }
  };

  const handlePdf = async () => {
    // Dibuka langsung saat klik supaya tidak diblokir popup blocker.
    const pdfWindow = window.open("", "_blank");

    try {
      setPendingAction("pdf");
      const response = await dosPdf(dos.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      if (pdfWindow) pdfWindow.location.href = url;
      else window.location.assign(url);

      setTimeout(() => URL.revokeObjectURL(url), PDF_URL_REVOKE_DELAY_MS);
    } catch (error) {
      pdfWindow?.close();
      if (!isForbiddenError(error)) {
        // Error endpoint PDF juga berupa Blob; httpError membacanya sebagai JSON.
        showError("Failed", await getApiErrorMessage(error, "Failed to load DOS PDF."));
      }
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {actions.edit && (
        <button
          onClick={() => navigate(`/dos/edit/${dos.id}`)}
          disabled={busy}
          className={`${baseButton} bg-yellow-100 text-yellow-800 hover:bg-yellow-200`}>
          Edit
        </button>
      )}

      {actions.delete && (
        <button
          onClick={handleDelete}
          disabled={busy}
          className={`${baseButton} bg-red-100 text-red-700 hover:bg-red-200`}>
          {pendingAction === "delete" ? "Deleting..." : "Delete"}
        </button>
      )}

      {actions.submit && (
        <button
          onClick={handleSubmit}
          disabled={busy}
          className={`${baseButton} bg-blue-600 text-white hover:bg-blue-700`}>
          {pendingAction === "submit" ? "Submitting..." : "Submit"}
        </button>
      )}

      {actions.approve && (
        <button
          onClick={handleApprove}
          disabled={busy}
          className={`${baseButton} bg-green-600 text-white hover:bg-green-700`}>
          {pendingAction === "approve" ? "Approving..." : "Approve"}
        </button>
      )}

      {actions.reject && (
        <button
          onClick={() => setRejectOpen(true)}
          disabled={busy}
          className={`${baseButton} bg-red-600 text-white hover:bg-red-700`}>
          Reject
        </button>
      )}

      {actions.convert && (
        <button
          onClick={handleConvert}
          disabled={busy}
          className={`${baseButton} bg-indigo-600 text-white hover:bg-indigo-700`}>
          {checkingQuotation ? (
            <>
              <Loader2 className="animate-spin" size={14} />
              Checking quotation...
            </>
          ) : pendingAction === "convert" ? (
            "Creating..."
          ) : (
            "Create Official DO"
          )}
        </button>
      )}

      {actions.pdf && (
        <button
          onClick={handlePdf}
          disabled={busy}
          className={`${baseButton} bg-blue-100 text-blue-700 hover:bg-blue-200`}>
          {pendingAction === "pdf" ? "Loading PDF..." : "PDF"}
        </button>
      )}

      <DosRejectDialog
        open={rejectOpen}
        dosNo={dos.dos_no}
        submitting={pendingAction === "reject"}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
      />
    </div>
  );
}
